import {
  builder,
  defaults,
  displayTrackersConfig
} from '../../../common/js/config/index';
import { isIos } from '../../../common/js/environment-utils';
import { units } from '../../../common/js/config/constants';
import { commonVideoConfig } from './config-common-video';

export const excludedMimeType = [
  'video/3gpp',
  'video/ogg',
  'video/x-flv',
  'video/quicktime'
];

export const unmarshaller = {
  isCreativeTransparent: builder.boolean('is_creative_transparent', {
    defaultValue: false
  }),
  watermarkApiKeys: defaults.watermarkApiKeys,
  recommendedLinks: defaults.recommendedLinks,
  contentDiscovery: defaults.contentDiscovery,
  omidTrackingEnabled: defaults.omidTrackingEnabled,
  formatBaseUrl: builder.string('format_base_url', { optional: true }),
  clickUrl: builder.string('click_url', { optional: true }),
  displayTrackers: displayTrackersConfig(),
  omidTrackers: defaults.omidTrackers,
  omidImpressionDelay: builder.number('omid_impression_delay', {
    defaultValue: 250,
    unit: units.milliseconds
  }),
  alternativeAutoplayDetect: builder.boolean('alternative_autoplay_detect', {
    defaultValue: false
  }),
  alternativeAutoplayLog: builder.boolean('alternative_autoplay_log', {
    defaultValue: false
  }),
  ignoreClicksBefore: builder.number('ignore_clicks_before', {
    defaultValue: 3000,
    description: 'Value in milliseconds',
    unit: units.milliseconds
  }),
  shouldCallMraidCloseOnClick: builder.boolean(
    'should_call_mraid_close_on_click',
    {
      defaultValue: false
    }
  ),
  customTranslations: builder.object('translations', {
    defaultValue: {},
    description: `Custom translations in JSON format.<br>Overridable fields are: 'discover', 'wait', 'wait_close', 'continue', 'click_bookmark', 'call_to_action' and this objects: 'skipOrContinue','confirmSkip','optIn' each containing 'message', 'confirm', 'cancel' <br>ex: '{"en": {"optIn": {"confirm": "please confirm"}}}'`
  }),
  locale: defaults.locale,
  browser: builder.holder({
    type: builder.string('browser', {
      possibleValues: [
        {
          name: 'Ogury browser',
          value: 'browser',
          comment: 'Internal delivery only'
        },
        {
          name: 'Default browser',
          value: 'defaultbrowser'
        }
      ],
      defaultValue: 'defaultbrowser',
      label: 'Browser',
      description:
        'Browser used to open the redirection url. Ogury Browser is only available for in-app ad formats.'
    }),
    preload: builder.boolean('browser_enable_preload', {
      defaultValue: true
    })
  }),
  interaction: builder.string('interaction', {
    of: ['touch', 'click'],
    defaultValue: 'click'
  }),
  brand: builder.holder({
    name: builder.string('brand_name', {
      label: 'Brand name',
      description:
        'Name of the brand, can be displayed over the creative and in a creative wording with #{brand} macro. It is also displayed on the Video Chooser preview',
      if: 'display_recommendation'
    })
  }),
  brandItem: builder.holder({
    show: builder.boolean('show_brand_item', {
      defaultValue: false,
      label: 'Show brand item',
      description: 'Show or hide the brand name and logo on thumbnail/top-ad'
    }),
    name: builder.string('brand_item_name', {
      label: 'Brand name',
      description:
        'Name of the brand, can be displayed over the creative and in a creative wording with #{brand} macro',
      optional: true,
      if: 'show_brand_item'
    }),
    logo: builder.string('brand_item_logo', {
      label: 'Brand logo',
      overrideType: 'image',
      description:
        'Logo of the brand, displayed over the creative. Recommended size 20x20px',
      optional: true,
      if: 'show_brand_item'
    })
  }),
  orientation: builder.string('orientation', {
    of: ['auto', 'landscape', 'portrait'],
    possibleValues: [
      {
        name: 'Auto',
        value: 'auto'
      },
      {
        name: 'Landscape',
        value: 'landscape'
      },
      {
        name: 'Portrait',
        value: 'portrait'
      }
    ],
    defaultValue: 'auto',
    label: 'Orientation',
    description: 'Specify device orientation to display the format'
  }),
  isVerticalVideo: builder.boolean('is_vertical_video', {
    defaultValue: false,
    label: 'Vertical Video',
    description:
      'Check this button if the creative is a vertical video. Will be used to optimize the display through the thumbnail video ad unit'
  }),
  showRecommendation: builder.boolean('display_recommendation', {
    defaultValue: true,
    label: 'Display Recommendation',
    description: 'Show or hide the Recommendation block'
  }),
  debug: builder.boolean('_debug', {
    defaultValue: false
  }),
  isProgrammatic: builder.boolean('is_programmatic', {
    label: 'Programmatic',
    description: 'Define if the creative is managed or programmatic',
    defaultValue: false
  }),
  video: builder.holder({
    creative: builder.string('video_creative_type', {
      of: ['vast', 'mp4'],
      defaultValue: 'vast',
      possibleValues: [
        {
          name: 'Vast URL',
          value: 'vast'
        },
        {
          name: 'MP4 Video',
          value: 'mp4'
        }
      ],
      label: 'Video creative'
    }),
    creativeRatio: builder.number('creative_ratio', {
      label: 'Creative Ratio',
      description: `Ratio of the Asset<br>
  Define if the creative orientation is portrait or landscape<br>
  Mandatory for Thumbnail Ad delivery<br>
  Default values are 1.78 for landscape and 0.56 for portrait for a 16:9 Asset<br>
  Automatically computed from uploaded Assets<br>
  Need to be manually defined for video programmatic, if not available in the video VAST or Adserver Redirect tags`,
      if: {
        valueOf: 'is_programmatic',
        is: false
      },
      unit: units.aspectRatio
    }),
    videoDuration: builder.number('video_duration', {
      label: 'Video duration',
      description: 'Duration of the video',
      if: {
        valueOf: 'is_programmatic',
        is: false
      },
      unit: units.seconds
    }),
    isBlurBackground: builder.boolean('video_blur_background', {
      defaultValue: false,
      label: 'Background Blur effect',
      description: 'Display blur background behind the creative'
    }),
    containerBackgroundColor: builder.color('container_background_color', {
      defaultValue: '#000000',
      label: 'Container Background Color',
      description:
        'Container background color behind the creative when the blur is deactivated'
    }),
    isBlurForcedDisabled: builder.boolean('force_blur_disabled', {
      defaultValue: false,
      label: 'Force Blur Disabled',
      description:
        'For Blur Disabled (override for default config values between interstitial and header/footer)'
    }),
    sendTracksOnClick: builder.boolean('all_quartiles_on_click', {
      defaultValue: true
    }),
    billedAfter: builder.number('completed_video_duration', {
      label: 'Completed video duration',
      description: 'Time in seconds to consider the video completed',
      allOf: [
        {
          valueOf: 'is_programmatic',
          is: false
        }
      ],
      unit: units.seconds
    }),
    soundOnStart: builder.boolean('sound_on_video_start', {
      defaultValue: false,
      label: 'Sound at startup',
      description: 'Set to Yes if the sound is played at video startup'
    }),
    vast: builder.string('vast', {
      allOf: [
        {
          valueOf: 'video_creative_type',
          is: 'vast'
        },
        {
          valueOf: 'is_programmatic',
          is: false
        }
      ],
      pattern: '^$|^https://.*$',
      message: 'is not a valid VAST URL'
    }),
    vastContent: builder.string('vast_content', {
      optional: true
    }),
    vastRawList: builder.arrayString('vast_raw_list', {
      optional: true,
      defaultValue: []
    }),
    cachedVideo: builder.string('vast_video_offline_url', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    vastVideo360p: builder.string('vast_video_360p', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    vastVideo480p: builder.string('vast_video_480p', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    vastVideo720p: builder.string('vast_video_720p', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    vastVideo1080p: builder.string('vast_video_1080p', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    vastVideoUrl: builder.string('vast_video_url', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    vastVideoSource: builder.string('vast_video_source', {
      if: {
        valueOf: 'video_creative_type',
        is: 'mp4'
      }
    }),
    forceVideoQuality: builder.string('force_video_quality', {
      optional: true
    }),
    enableBase64Download: builder.boolean('enable_base64_download', {
      defaultValue: false
    }),
    forceDisableBase64Download: builder.boolean(
      'force_disable_base64_download',
      {
        defaultValue: false
      }
    ),
    enableLinkPreload: builder.boolean('enable_link_preload', {
      defaultValue: false
    }),
    useCategoryName: builder.boolean('use_iab_category', {
      defaultValue: true,
      label: 'Display IAB Category Name',
      description: 'Show or hide category name in ad chooser format.'
    }),
    categoryName: builder.string('category_name', {
      optional: true,
      label: 'IAB Category name',
      description: 'Category name to be visible in ad chooser format.',
      if: 'use_iab_category'
    }),
    iabCategoryCode: builder.string('iab_category_code', {
      optional: true
    }),
    thumbnail: builder.holder({
      previewUrl: builder.string('thumbnail_preview_url', {
        optional: true
      }),
      percentX: builder.number('thumbnail_percent_x', {
        defaultValue: -1
      }),
      percentY: builder.number('thumbnail_percent_y', {
        defaultValue: -1
      }),
      percentWidth: builder.number('thumbnail_percent_width', {
        defaultValue: 1
      }),
      percentHeight: builder.number('thumbnail_percent_height', {
        defaultValue: 1
      }),
      time: builder.number('thumbnail_time', {
        defaultValue: 1,
        unit: units.seconds
      }),
      objectFit: builder.string('thumbnail_object_fit', {
        possibleValues: [
          {
            name: 'Cover',
            value: 'cover'
          },
          {
            name: 'Contain',
            value: 'contain'
          }
        ],
        defaultValue: 'cover'
      })
    }),
    enableOpacityOnShow: builder.boolean('enable_video_opacity_on_show', {
      defaultValue: true
    }),
    debugVastContent: builder.boolean('debug_vast_content', {
      defaultValue: false
    }),
    vastOptions: builder.holder({
      timeout: builder.number('vast_timeout', {
        optional: true,
        label: 'VAST Timeout',
        description:
          'Timeout in milliseconds for each VAST request. If not set, default value will be used.',
        unit: units.milliseconds
      }),
      timeoutStrategy: builder.string('vast_timeout_strategy', {
        optional: true,
        of: ['exponential', 'fixed'],
        possibleValues: [
          {
            name: 'Exponential',
            value: 'exponential',
            comment: 'Timeout increases exponentially with each retry'
          },
          {
            name: 'Fixed',
            value: 'fixed',
            comment: 'Timeout remains constant for all retries'
          }
        ],
        label: 'VAST Timeout Strategy',
        description:
          'Strategy for timeout handling: exponential (timeout * 2^n) or fixed (constant timeout)'
      }),
      retries: builder.number('vast_retries', {
        optional: true,
        defaultValue: 2,
        label: 'VAST Retries',
        description:
          'Number of retries for each VAST wrapper fetch. Default: 2 (3 total attempts per wrapper)'
      }),
      retryDelay: builder.number('vast_retry_delay', {
        optional: true,
        defaultValue: 0,
        label: 'VAST Retry Delay',
        description:
          'Delay in milliseconds between retries. Default: 0. If timeoutStrategy is exponential and retryDelay is not set, delay will be calculated automatically.',
        unit: units.milliseconds
      }),
      maxTotalTime: builder.number('vast_max_total_time', {
        optional: true,
        label: 'VAST Max Total Time',
        description:
          'Maximum total time in milliseconds for the entire VAST chain (all wrappers). If not set, there is no limit. Recommended: 30000 (30 seconds) to respect timing constraints.',
        unit: units.milliseconds
      })
    }),
    preload: builder.string('video_preload_attribute', {
      defaultValue: isIos() ? 'auto' : 'none'
    }),
    maxFileSize: builder.number('video_max_file_size', {
      defaultValue: 10000,
      unit: units.kilobytes
    }),
    chooseBestVideo: builder.boolean('choose_best_video', {
      defaultValue: false
    }),
    newVideoSelection: builder.boolean('new_video_selection', {
      defaultValue: true
    }),
    ...commonVideoConfig
  }),
  icon: builder.holder({
    useIcon: builder.boolean('use_icon', {
      defaultValue: false,
      label: 'Icon',
      description:
        'Creates an icon on the user homescreen opening the browser with the landing page. Only works for Android < 8.0.0'
    }),

    url: builder.icon('icon', {
      overrideType: 'image',
      label: 'Icon file',
      description:
        'Upload a PNG or JPG, min 256*256px. We recommend a square picture, otherwise, it will be cropped.',
      if: 'use_icon'
    }),

    name: builder.string('icon_name', {
      label: 'Icon name',
      description: 'Application name',
      if: 'use_icon'
    }),

    size: builder.number('icon_size', {
      defaultValue: 75,
      unit: units.pixel
    }),

    bannerHeight: builder.number('icon_banner_height', {
      defaultValue: 96,
      description: 'Height of the icon banner zone',
      unit: units.pixel
    }),

    backgroundColor: builder.color('icon_banner_background_color', {
      defaultValue: '#AAAAAA',
      description: 'Background color of the icon banner zone'
    }),

    uppercase: builder.boolean('icon_uppercase', {
      defaultValue: true
    }),

    identifier: builder.string('identifier', {
      overrideType: 'hidden',
      label: 'Icon identifier',
      description:
        'If two campaigns have the same identifier, when the user install the icon for the second time it will be replaced instead of duplicated.',
      if: 'use_icon',
      optional: true
    }),

    isAllowedAfterBrowser: builder.boolean('allow_icon_after_browser', {
      defaultValue: true
    })
  }),
  closeBtn: builder.holder({
    useCrossCloseButton: builder.boolean('cross_close_btn', {
      defaultValue: false,
      description:
        'Enable a cross button instead of continue button to close the interstitial. Set to true when use_cta = true.',
      label: 'Use cross close button'
    }),
    hideOnOverlay: builder.boolean('unskippable_overlay', {
      defaultValue: false,
      description: 'Prevent to display a close button on overlay'
    }),
    addSdkCloseButton: builder.boolean('close_btn_use_sdk', {
      defaultValue: false,
      description: 'Do not ask the SDK to hide it‘s default close button'
    }),
    forceImmediate: builder.boolean('close_btn_force_immediate', {
      defaultValue: false,
      description: 'Force to display the close button on video start'
    }),
    size: builder.number('close_btn_size', {
      defaultValue: 100,
      label: 'Close button size',
      description: 'Size of the close button in %',
      unit: units.percentage
    }),
    displayAfter: builder.number('close_btn_display_after', {
      defaultValue: 8,
      label: 'Close button delay',
      description:
        'Allow to control the delay before displaying the close button on the ad. Cannot exceed 15 seconds for Android in-app delivery. Rewarded video is not impacted by this limitation',
      unit: units.seconds
    }),
    autoCloseDelay: builder.number('auto_close_timeout', {
      defaultValue: 0,
      label: 'Auto close delay',
      description:
        'Number of seconds before closing the format after the video reaches its end',
      unit: units.seconds
    }),
    closeDelay: builder.number('video_close_delay', {
      defaultValue: 0,
      label: 'Video close delay',
      description:
        'Number of seconds to really close the format after the click on the close button. This delay is applied if the video wasn‘t considered completed',
      unit: units.seconds
    }),
    showCloseDelay: builder.boolean('show_video_close_text', {
      defaultValue: true,
      label: 'Close text display',
      description: 'Display or not the close text "Close in x sec"'
    })
  }),

  controls: builder.holder({
    layout: builder.holder({
      canDisplayResponsiveControls: builder.boolean(
        'can_display_responsive_controls',
        { defaultValue: false }
      )
    }),
    sound: builder.holder({
      enabled: builder.boolean('display_sound_button', {
        defaultValue: true,
        label: 'Display sound button',
        description: 'Show or hide the sound button'
      })
    }),
    backgroundColor: builder.color('controls_background_color', {
      defaultValue: '#28323c',
      label: 'Background color',
      description: 'Background color of the controls'
    }),
    backgroundOpacity: builder.number('controls_background_opacity', {
      defaultValue: 50,
      label: 'Background opacity',
      description: 'Background opacity (%) of the controls',
      unit: units.percentage
    }),
    labelColor: builder.color('controls_label_color', {
      defaultValue: '#FFFFFF',
      label: 'Label color',
      description: 'Color of the controllers labels'
    }),
    labelOpacity: builder.number('controls_label_opacity', {
      defaultValue: 100,
      label: 'Label opacity',
      description: 'Opacity (%) of the controllers labels',
      unit: units.percentage
    }),
    responsiveBorderHidden: builder.boolean('responsive_border_hidden', {
      defaultValue: false,
      label: 'Responsive Border Hidden',
      description:
        'A boolean variable that, when set to true, hides the border of the responsive component. Set it to false to keep the border visible.'
    }),
    showProgressBar: builder.boolean('show_progress_bar', {
      defaultValue: true
    })
  }),
  skin: builder.holder({
    visibleByDefault: builder.boolean('skins_visible_by_default', {
      optional: true,
      defaultValue: false,
      label: 'Skins visible by default',
      description:
        'Can have the skins visible by default (when ad is not expanded)'
    }),
    endScreenImage: builder.string('end_screen_image', {
      optional: true,
      overrideType: 'image',
      label: 'End screen image',
      description:
        'To be visible “auto close delay“ should be greater than 0. Will be displayed on the last frame of the video. Make sure that the picture respects the ratio of the video. Recommended size is 1280px * 720px.'
    }),
    endScreenHtml: builder.string('end_screen_tag_redirect', {
      optional: true,
      label: 'End screen tag redirect',
      description:
        'To be visible “auto close delay“ should be greater than 0. Will be displayed on the last frame of the video. Make sure that the tag redirect respects the ratio of the video. Recommended size is 1280px * 720px.'
    }),
    skinTop: builder.string('skin_top_image', {
      optional: true,
      overrideType: 'image',
      label: 'Skin top file',
      description:
        'Will be displayed on the top of the screen (won‘t cover the video)'
    }),
    skinBottom: builder.string('skin_bottom_image', {
      optional: true,
      overrideType: 'image',
      label: 'Skin bottom file',
      description:
        'Will be displayed on the bottom of the screen (won‘t cover the video)'
    })
  }),
  global: builder.holder({
    sdkVersion: defaults.sdkVersion,
    odid: defaults.odid,
    application: defaults.application
  }),
  /**
   * Analytics
   */
  analytics: builder.holder({
    enable: builder.boolean('analytics_enable', {
      label: 'Enable analytics',
      description: 'Allow sending of analytics tracks',
      defaultValue: false
    })
  }),
  enableRealQuartiles: builder.boolean('enable_real_quartiles', {
    description: 'Allow sending real quartiles',
    defaultValue: false
  }),

  skipAfterDelay: builder.holder({
    enabled: builder.boolean(`skip_or_continue_after_delay_display`, {
      defaultValue: false,
      label: 'After delay layer',
      description: 'Display a layer after 15s of video viewed'
    }),
    delay: builder.number(`skip_or_continue_after_delay_delay`, {
      defaultValue: 10,
      description: 'Time in second before dual button will shows up',
      if: 'skip_or_continue_after_delay_display',
      label: 'After delay layer delay',
      unit: units.seconds
    }),
    backgroundColor: builder.color(
      `skip_or_continue_after_delay_background_color`,
      {
        defaultValue: '#888888',
        description: 'Color of the dual button modal background',
        if: 'skip_or_continue_after_delay_display',
        label: 'After delay layer color'
      }
    ),
    backgroundOpacity: builder.number(
      `skip_or_continue_after_delay_background_opacity`,
      {
        defaultValue: 60,
        description: 'Opacity in percent of the dual button modal background',
        if: 'skip_or_continue_after_delay_display',
        label: 'After delay layer opacity',
        unit: units.percentage
      }
    )
  }),
  confirmSkip: builder.holder({
    enabled: builder.boolean(`on_skip_confirmation_display`, {
      defaultValue: true,
      label: 'On click layer',
      description: 'Display a layer after a click on the close button'
    }),
    delay: builder.number(`on_skip_confirmation_delay`, {
      defaultValue: 10,
      description: 'Time in second before dual button will shows up',
      if: 'on_skip_confirmation_display',
      unit: units.seconds
    }),
    backgroundColor: builder.color(`on_skip_confirmation_background_color`, {
      defaultValue: '#888888',
      description: 'Color of the dual button modal background',
      if: 'on_skip_confirmation_display',
      label: 'On click layer color'
    }),
    backgroundOpacity: builder.number(
      `on_skip_confirmation_background_opacity`,
      {
        defaultValue: 60,
        description: 'Opacity in percent of the dual button modal background',
        if: 'on_skip_confirmation_display',
        label: 'On click layer opacity',
        unit: units.percentage
      }
    )
  }),
  omidVideoTrackers: builder.string('omid_video_trackers', {
    defaultValue: '',
    description: 'OMID Video trackers'
  }),
  displayVastIcon: builder.boolean('display_vast_icon', {
    defaultValue: false,
    description: 'Display vast icon (adchoices)'
  }),
  hasVpaidTracking: builder.boolean('vpaid_tracking', {
    defaultValue: false
  }),
  optOut: builder.holder({
    hide: builder.boolean('hide_opt_out', {
      defaultValue: false,
      description: 'Hide opt out ONLY IF display vast icon (adchoices)'
    }),
    closeAdOnClick: builder.boolean('close_ad_on_optout_click', {
      defaultValue: true,
      description:
        'Close the ad when the user clicks on the opt-out link. If false, the ad will not be closed.'
    })
  }),
  cta: builder.holder({
    useCta: builder.boolean('use_cta', {
      defaultValue: false,
      label: 'Use CTA display',
      description:
        'Enable the click to action button display. Video is not anymore clickable but there is a button to visit the website. The close button becomes a cross close button.'
    }),
    backgroundColor: builder.color('cta_background_color', {
      defaultValue: '#4185F4',
      label: 'CTA Background color',
      description: 'Change the background color of the cta button.',
      if: 'use_cta'
    }),
    labelColor: builder.color('cta_label_color', {
      defaultValue: '#FFFFFF',
      label: 'CTA Text color',
      description: 'Change the label color of the cta button.',
      if: 'use_cta'
    }),
    reverseTopBar: builder.boolean('cta_reverse_top_bar', {
      defaultValue: true,
      label: 'Reverse top bar',
      description:
        'Reverse the top bar controls. Close button is now on the top-left corner and the mute icon on the top-right corner. Only for CTA.',
      if: 'use_cta'
    }),
    timeBeforeFadeOut: builder.number('cta_time_before_fade_out', {
      defaultValue: 4,
      min: 3,
      label: 'Time before fade out',
      description:
        'For the fade out system, this is the time before fading out all the controls in seconds. You need to activate the fade out before.',
      if: 'use_cta',
      unit: units.seconds
    }),
    useFadeOut: builder.boolean('cta_use_fade_out', {
      defaultValue: true,
      label: 'Use fade out',
      description:
        'Enable the fade out system which is waiting x seconds before fading out all the controls. Only for CTA.',
      if: 'use_cta'
    })
  }),
  forceClickOnTouch: builder.boolean('force_click_ontouch', {
    defaultValue: false,
    description: 'Allow the user to click on the ontouch event'
  }),
  endScreen: builder.holder({
    allowListType: builder.arrayString('end_screen_allow_list_type', {
      defaultValue: [],
      description:
        'A list indicating the type of end screen it is compatible with.',
      possibleValues: [
        {
          name: 'APP_PREVIEW',
          value: 'app_preview'
        },
        {
          name: 'DEFAULT',
          value: 'default'
        },
        {
          name: 'HTML',
          value: 'html'
        },
        {
          name: 'IFRAME',
          value: 'iframe'
        },
        {
          name: 'IMAGE',
          value: 'image'
        }
      ]
    }),
    forced: builder.boolean('force_vast_end_screen', {
      defaultValue: false
    }),
    duration: builder.number('end_screen_duration', {
      defaultValue: 120,
      label: 'End screen duration',
      description:
        'Number of seconds before closing the format after the video reaches its end when having an end screen content given by the vast',
      unit: units.seconds
    }),
    ctaOverlay: builder.boolean('end_screen_cta_overlay', {
      defaultValue: false
    }),
    closeBtn: builder.holder({
      delay: builder.number('end_screen_close_btn_delay', {
        defaultValue: 0,
        unit: units.seconds
      }),
      size: builder.number('end_screen_close_btn_size', {
        defaultValue: 100,
        unit: units.pixel
      })
    })
  }),
  skOverlay: builder.holder({
    displayAfter: builder.number('skoverlay_display_after', {
      defaultValue: 3,
      label: 'SKOverlay delay',
      description: 'Delay in seconds before displaying the skoverlay on the ad',
      unit: units.seconds,
      hide: true
    })
  }),
  isMraidCompliant: builder.boolean('is_mraid_compliant', {
    label: 'Is Mraid Compliant',
    description:
      'Determines if the format is Mraid compliant. If not, the format will need to polyfill the Mraid API',
    defaultValue: true
  }),
  isOgurySdk: builder.boolean('is_ogury_sdk', {
    label: 'Is Ogury SDK',
    description: 'Determines if the format is delivered through Ogury SDK',
    defaultValue: true
  })
};

export const formatConfigUnmarshaller = {
  id: 'video-sdk3',
  config: unmarshaller
};
